<template>
  <base-invite
    v-on="$listeners"
    :handleSuccessButtonClick="handleConfirmInvite"
    :backToUsers="backToUsers"
    :successButtonLabel="$t('common.continue')"
    :canSubmit="canSubmit"
    :primaryLoading="primaryLoading"
  >
    <template v-slot:invite-header>
      <h3>{{ $t("organizations.invite_users.import.title") }}</h3>
    </template>
    <template v-slot:invite-body>
      <el-form :rules="rules" :model="form" ref="import">
        <el-form-item class="uploader" name="fileList">
          <div class="upload-instructions">
            <p>{{ $t("organizations.invite_users.import.instructions2") }}</p>
            <a href="/files/ELSA-Userlist.xlsx" download>{{
              $t("organizations.invite_users.import.get_template_xlsx")
            }}</a>
            <p>{{ $t("common.or") }}</p>
            <a href="/files/ELSA-Userlist.csv" download>{{
              $t("organizations.invite_users.import.get_template_csv")
            }}</a>
            <p :style="{ 'margin-left': '0px' }">,</p>
            <p :style="{ 'margin-left': '0px' }">{{ $t("organizations.invite_users.import.instructions3") }}</p>
          </div>

          <el-upload
            v-loading="loading"
            class="upload-files"
            :action="actionUrl"
            :before-upload="getActionUrl"
            :http-request="submitFile"
            :on-success="onSuccess"
            v-bind:class="{ 'hide-uploader': hideUploader, 'hide-file-list': hideFileList }"
            :drag="true"
            :file-list="form.fileList"
            :show-file-list="true"
            :auto-upload="true"
            :on-change="updateFileList"
            :limit="1"
            :on-remove="updateFileList"
          >
            <i class="elsa-icon-import-grey"></i>
            <div class="el-upload__text">
              {{ $t("organizations.invite_users.import.instructions") }}
              <p>{{ $t("common.or") }}</p>
              <chip icon="">
                {{ $t("organizations.invite_users.import.browse_files") }}
              </chip>
            </div>
          </el-upload>
        </el-form-item>
        <elsa-select
          name="selectedRole"
          v-if="selectRolesAvailable"
          v-model.lazy="form.selectedRole"
          :label="$t('organizations.users_section.modals.invite_users.select_role_label')"
          :placeholder="$t('organizations.users_section.modals.invite_users.select_role_placeholder')"
          :options="availableRoles"
          class="invite-email-select-role"
        />
        <el-form-item class="consent" prop="consent" show-message>
          <el-checkbox class="elsa-checkbox" label="consent" v-model.lazy="form.consent">
            {{ $t("organizations.users_section.modals.invite_users.consent1", { organizationName: organizationName }) }}
          </el-checkbox>
        </el-form-item>
      </el-form>
    </template>
  </base-invite>
</template>

<script>
  import BaseInvite from "./BaseInvite";
  import Chip from "@/components/common/Chip";
  import ElsaSelect from "@/components/common/Select";
  import { mapActions } from "vuex";
  import { ADMIN, STUDENT, selectUserRoles } from "@/constants/userRoles";
  import { propEq, reject, isEmpty, compose, pathOr, includes, length, defaultTo, path } from "ramda";
  import { isPropEmpty } from "@/helpers/formValidationHelper";
  import { warningMessage, successMessage } from "@/utils/messages";
  import { readFile, uploadFile, allowedTypes } from "@/utils/uploader";

  export default {
    components: {
      BaseInvite,
      Chip,
      ElsaSelect,
    },
    props: {
      role: {
        type: String,
        default: STUDENT,
      },
      selectRolesAvailable: {
        type: Boolean,
        default: false,
      },
      organizationId: {},
      groupId: {},
    },
    data() {
      return {
        primaryLoading: false,
        errors: null,
        canSubmit: false,
        actionUrl: "",
        uploadId: "",
        loading: false,
        numberOfEmails: 0,
        form: {
          fileList: [],
          selectedRole: "",
          fileReady: false,
          consent: true,
        },
        rules: {
          fileList: [
            {
              required: true,
            },
          ],
          selectedRole: [
            {
              required: true,
            },
          ],
        },
      };
    },
    methods: {
      async getActionUrl(file) {
        this.loading = true;
        this.errors = false;
        const fileSize = file.size;
        const contentType = file.type;
        const organizationId = this.organizationId;
        const groupId = this.groupId;
        const fileName = file.name;
        const payload = {
          fileSize,
          contentType,
          organizationId,
          groupId,
          fileName,
        };

        if (!includes(contentType, allowedTypes)) {
          this.errors = true;
          this.updateFileList(null, []);
          this.loading = false;
          warningMessage(this.$t("common.file_type_not_allowed"))(this);
          return Promise.reject(false);
        }

        if (!this.selectRolesAvailable) {
          payload.role = this.role;
        }

        try {
          const req = await this.getInviteUploadUrl({
            ...payload,
          });

          this.actionUrl = req.data.presigned_url;
          this.uploadId = req.data.id;
        } catch (e) {
          // show warning
          // please try again
          this.errors = true;
          this.updateFileList(null, []);
          this.loading = false;
          warningMessage(this.$t("common.generic_error_message"))(this);
          return Promise.reject(false);
        }
      },
      async submitFile(request) {
        try {
          const { action: url, file } = request;
          const rawFile = await readFile(file);
          return await uploadFile(url, file.type, rawFile);
        } catch (e) {
          this.errors = true;
          this.loading = false;
          return warningMessage(this.$t("common.generic_error_message"))(this);
        }
      },
      // eslint-disable-next-line no-unused-vars
      async onSuccess(response, file, fileList) {
        try {
          const req = await this.getInviteUploadEmails({
            uploadId: this.uploadId,
          });

          if (compose(isEmpty, pathOr([], ["data", "emails"]))(req)) {
            this.loading = false;
            return warningMessage(this.$t("common.generic_error_message"))(this);
          }

          this.numberOfEmails = compose(length, pathOr([], ["data", "emails"]))(req);
          this.form.fileReady = true;
          this.loading = false;
        } catch (e) {
          // show warning
          this.errors = true;
          this.loading = false;
          return warningMessage(this.$t("common.generic_error_message"))(this);
        }
      },
      handleConfirmInvite() {
        this.$confirm(this.messageConfirm, this.$t("organizations.invite_users.confirmation_title"), {
          confirmButtonText: this.$t("student.yes"),
          cancelButtonText: this.$t("student.no"),
          dangerouslyUseHTMLString: true,
          type: "warning",
        })
          .then(() => {
            this.handleInvite();
          })
          .catch(() => {
            return;
          });
      },
      async handleInvite() {
        this.primaryLoading = true;
        if (this.selectRolesAvailable) {
          try {
            await this.patchInviteUploadEmails({
              uploadId: this.uploadId,
              data: { role: this.form.selectedRole },
            });
          } catch (e) {
            return warningMessage(this.$t("common.generic_error_message"))(this);
          }
        }

        try {
          this.postSentInviteUploadEmails({ uploadId: this.uploadId, consent: this.form.consent });
        } catch (e) {
          return warningMessage(this.$t("common.generic_error_message"))(this);
        }
        // show success message
        this.primaryLoading = false;
        if (this.form.consent) {
          successMessage(
            this.$t("organizations.invite_users.multiple_mail_invite_with_enrolled", {
              numberOfInvites: this.numberOfEmails,
            })
          )(this);
        } else {
          successMessage(
            this.$t("organizations.invite_users.multiple_mail_invite", { numberOfInvites: this.numberOfEmails })
          )(this);
        }
        return this.backToUsers({ invited: true });
      },
      backToUsers(event = {}) {
        this.$emit("toggle-organization-nav", true);
        this.$emit("invite-users", "users");
        this.$emit("closed", event.invited);
      },
      updateFileList(file, fileList) {
        this.form.fileList = fileList;
      },
      ...mapActions("invite", [
        "getInviteUploadUrl",
        "getInviteUploadEmails",
        "updateInviteUploadEmails",
        "postSentInviteUploadEmails",
        "patchInviteUploadEmails",
      ]),
    },
    computed: {
      messageConfirm() {
        return this.form.consent
          ? this.$t(this.$i18nLabelsHelper(this.currentOrganizationType, "auto_enroll_message_confirm"))
          : this.$t(this.$i18nLabelsHelper(this.currentOrganizationType, "accept_invitation_enroll_message_confirm"));
      },
      hideUploader() {
        return this.form.fileList.length > 0;
      },
      hideFileList() {
        return this.form.fileList.length === 0;
      },
      availableRoles() {
        return reject(propEq("value", ADMIN), selectUserRoles);
      },
      organizationName() {
        const organizationName = compose(
          defaultTo(this.$store.getters["organization/currentOrganization"].name),
          path(["organizationData", "name"])
        )(this);

        return organizationName;
      },
    },
    watch: {
      form: {
        immediate: true,
        deep: true,
        handler(newForm) {
          this.canSubmit =
            newForm.fileReady && (!isPropEmpty("selectedRole")(newForm) || !this.selectRolesAvailable) && !this.errors;
        },
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "../../../scss/_colors.scss";

  .upload-demo {
    width: 500px;
    height: 500px;
  }

  .update-header {
    font-weight: 500;
    font-size: 18px;
    text-transform: capitalize;
    color: #546873;
  }

  .invite-email-select-role {
    padding-top: 32px;
  }

  .consent {
    padding-top: 20px;
    padding-bottom: 32px;
  }
</style>
